/**
 * Extracts JWT token from URL search parameters and stores it in localStorage
 *
 * @param paramName - The name of the URL parameter containing the JWT token (default: 'mobileAppJWT')
 * @returns The extracted token or null if not found
 * @throws Error if localStorage is not available or token storage fails
 * @example
 * const token = extractAndStoreMobileJWTToken();
 * if (token) {
 *   // Token extracted and stored successfully
 * }
 */
export function extractAndStoreMobileJWTToken(paramName: string = 'mobileAppJWT'): string | null {
  // Check if we're in a browser environment

  if (typeof window === 'undefined') {
    return null;
  }

  // Check if localStorage is available
  if (typeof Storage === 'undefined' || typeof localStorage === 'undefined') {
    throw new Error('localStorage is not available in this environment');
  }

  // Extract token from URL parameters
  const urlParams = new URLSearchParams(window.location.search);
  const token = urlParams.get(paramName);

  if (!token) {
    return null;
  }

  // Validate token format (basic JWT structure check)
  if (!isValidJWTFormat(token)) {
    throw new Error('Invalid JWT token format');
  }

  // Store token in localStorage
  localStorage.setItem('token', token);

  return token;
}

/**
 * Validates JWT payload by decoding and parsing as JSON
 *
 * @param payload - The base64 encoded payload part of the JWT
 * @returns True if payload can be decoded and parsed as valid JSON
 */
export function validateJWTPayload(payload: string): boolean {
  // Decode the payload and validate it's valid JSON
  // Add padding if needed for base64 decoding
  const paddedPayload = payload + '='.repeat((4 - (payload.length % 4)) % 4);
  const decodedPayload = atob(paddedPayload);

  // Try to parse as JSON to ensure it's valid
  JSON.parse(decodedPayload);

  return true;
}

/**
 * Validates JWT token format and decodes payload to ensure it's valid JSON
 *
 * @param token - The token to validate
 * @returns True if token has valid JWT structure and decodable JSON payload
 */
export function isValidJWTFormat(token: string): boolean {
  if (!token || typeof token !== 'string') {
    return false;
  }

  // JWT should have exactly 3 parts separated by dots
  const parts = token.split('.');
  if (parts.length !== 3 || !parts.every((part) => part.length > 0)) {
    return false;
  }

  try {
    return validateJWTPayload(parts[1]);
  } catch {
    // If decoding or JSON parsing fails, the token is invalid
    return false;
  }
}
